import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { format, formatDistanceToNow } from "date-fns"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiDownload,
  FiLogOut,
  FiRefreshCw,
  FiShield,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import { useSemanticColors } from "@/hooks/useSemanticColors"

type AuthEventType =
  | "LOGIN_SUCCESS"
  | "LOGIN_FAILURE"
  | "LOGOUT"
  | "PASSWORD_CHANGE"
  | "PASSWORD_RESET_REQUEST"
  | "PASSWORD_RESET_COMPLETE"
  | "SESSION_IP_CHANGE"
  | "LICENSE_CREATED"
  | "LICENSE_REVOKED"
  | "LICENSE_DELETED"

type AuthEvent = {
  id: string
  created_at: string
  user_id: string | null
  email_attempted: string
  event_type: AuthEventType
  ip_address: string | null
  ip_chain: string[] | null
  user_agent: string | null
  failure_reason: string | null
  event_metadata: Record<string, unknown> | null
}

type UserSessionRow = {
  id: string
  user_id: string
  token_jti: string
  started_at: string
  last_activity_at: string
  ended_at: string | null
  ended_reason: string | null
  ip_address: string | null
  ip_chain: string[] | null
  user_agent: string | null
  ip_change_count: number
  user_email: string
  user_full_name: string | null
  user_is_superuser: boolean
}

type SessionActivityRow = {
  id: string
  session_id: string
  at: string
  method: string
  path: string
  status_code: number
  resource_type: string | null
  resource_id: string | null
  duration_ms: number
}

type AuditSearch = {
  user_id?: string
  email?: string
  ip?: string
  event_type?: AuthEventType
  failure_reason?: string
  start?: string
  end?: string
  page?: number
  pageSize?: number
  sortDesc?: boolean
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500]

const eventColor: Record<
  AuthEventType,
  "green" | "red" | "gray" | "blue" | "orange" | "purple" | "yellow"
> = {
  LOGIN_SUCCESS: "green",
  LOGIN_FAILURE: "red",
  LOGOUT: "gray",
  PASSWORD_CHANGE: "blue",
  PASSWORD_RESET_REQUEST: "orange",
  PASSWORD_RESET_COMPLETE: "purple",
  SESSION_IP_CHANGE: "yellow",
  LICENSE_CREATED: "green",
  LICENSE_REVOKED: "orange",
  LICENSE_DELETED: "red",
}

const EVENT_TYPES: AuthEventType[] = [
  "LOGIN_SUCCESS",
  "LOGIN_FAILURE",
  "LOGOUT",
  "PASSWORD_CHANGE",
  "PASSWORD_RESET_REQUEST",
  "PASSWORD_RESET_COMPLETE",
  "SESSION_IP_CHANGE",
  "LICENSE_CREATED",
  "LICENSE_REVOKED",
  "LICENSE_DELETED",
]

function buildQuery(params: Record<string, string | number | boolean | undefined>) {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue
    sp.set(k, String(v))
  }
  const q = sp.toString()
  return q ? `?${q}` : ""
}

function apiFetch(path: string) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
}

export const Route = createFileRoute("/_layout/admin-audit")({
  validateSearch: (search: Record<string, unknown>): AuditSearch => ({
    user_id: typeof search.user_id === "string" ? search.user_id : undefined,
    email: typeof search.email === "string" ? search.email : undefined,
    ip: typeof search.ip === "string" ? search.ip : undefined,
    event_type:
      typeof search.event_type === "string" &&
      EVENT_TYPES.includes(search.event_type as AuthEventType)
        ? (search.event_type as AuthEventType)
        : undefined,
    failure_reason:
      typeof search.failure_reason === "string"
        ? search.failure_reason
        : undefined,
    start: typeof search.start === "string" ? search.start : undefined,
    end: typeof search.end === "string" ? search.end : undefined,
    page: typeof search.page === "number" ? search.page : 1,
    pageSize:
      typeof search.pageSize === "number" ? search.pageSize : 50,
    sortDesc: typeof search.sortDesc === "boolean" ? search.sortDesc : true,
  }),
  component: AdminAudit,
})

function AdminAudit() {
  const sc = useSemanticColors()
  const { t } = useTranslation("admin")
  const navigate = useNavigate({ from: "/admin-audit" })
  const search = Route.useSearch()

  const queryClient = useQueryClient()
  const [showLocal, setShowLocal] = useState(false)
  // Per-session expand state for the activities drawer
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null)

  // Local draft state for inputs, applied on Enter / Apply
  const [draftEmail, setDraftEmail] = useState(search.email ?? "")
  const [draftIp, setDraftIp] = useState(search.ip ?? "")
  const [draftReason, setDraftReason] = useState(search.failure_reason ?? "")
  const [draftStart, setDraftStart] = useState(search.start ?? "")
  const [draftEnd, setDraftEnd] = useState(search.end ?? "")

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? 50
  const skip = (page - 1) * pageSize

  const queryParams = useMemo(
    () => ({
      limit: pageSize,
      skip,
      user_id: search.user_id,
      email: search.email,
      ip: search.ip,
      event_type: search.event_type,
      failure_reason: search.failure_reason,
      start_time: search.start,
      end_time: search.end,
      sort_desc: search.sortDesc ?? true,
    }),
    [pageSize, skip, search],
  )

  const eventsQuery = useQuery({
    queryKey: ["admin-audit-events", queryParams],
    queryFn: () =>
      apiFetch(`/api/v1/admin/auth-events${buildQuery(queryParams)}`).then(
        (r) => {
          if (!r.ok) throw new Error("Failed to load auth events")
          return r.json() as Promise<AuthEvent[]>
        },
      ),
    refetchInterval: 60_000,
  })

  const countQuery = useQuery({
    queryKey: [
      "admin-audit-events-count",
      {
        user_id: search.user_id,
        email: search.email,
        ip: search.ip,
        event_type: search.event_type,
        failure_reason: search.failure_reason,
        start_time: search.start,
        end_time: search.end,
      },
    ],
    queryFn: () =>
      apiFetch(
        `/api/v1/admin/auth-events/count${buildQuery({
          user_id: search.user_id,
          email: search.email,
          ip: search.ip,
          event_type: search.event_type,
          failure_reason: search.failure_reason,
          start_time: search.start,
          end_time: search.end,
        })}`,
      ).then((r) => {
        if (!r.ok) throw new Error("Failed to load count")
        return r.json() as Promise<{ count: number }>
      }),
  })

  const total = countQuery.data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Active sessions panel — independent of the auth-events filter set.
  const activeSessionsQuery = useQuery({
    queryKey: ["admin-active-sessions", search.user_id],
    queryFn: () =>
      apiFetch(
        `/api/v1/admin/sessions${buildQuery({
          active_only: true,
          limit: 200,
          user_id: search.user_id,
        })}`,
      ).then((r) => {
        if (!r.ok) throw new Error("Failed to load sessions")
        return r.json() as Promise<UserSessionRow[]>
      }),
    refetchInterval: 30_000,
  })

  // Count of active sessions per user_id, to flag concurrent logins.
  const activeCountByUser = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of activeSessionsQuery.data ?? []) {
      m.set(s.user_id, (m.get(s.user_id) ?? 0) + 1)
    }
    return m
  }, [activeSessionsQuery.data])

  const activitiesQuery = useQuery({
    queryKey: ["admin-session-activities", expandedSessionId],
    queryFn: () =>
      apiFetch(
        `/api/v1/admin/sessions/${expandedSessionId}/activities?limit=200`,
      ).then((r) => {
        if (!r.ok) throw new Error("Failed to load activities")
        return r.json() as Promise<SessionActivityRow[]>
      }),
    enabled: !!expandedSessionId,
  })

  const revokeMutation = useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch(`/api/v1/admin/sessions/${sessionId}/revoke`).then((r) => {
        if (!r.ok) throw new Error("Failed to revoke session")
        return r.json()
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-active-sessions"] })
      queryClient.invalidateQueries({ queryKey: ["admin-audit-events"] })
    },
  })

  const updateSearch = (patch: Partial<AuditSearch>) => {
    navigate({
      search: (prev) => ({ ...prev, ...patch }),
    })
  }

  const applyFilters = () => {
    updateSearch({
      email: draftEmail || undefined,
      ip: draftIp || undefined,
      failure_reason: draftReason || undefined,
      start: draftStart || undefined,
      end: draftEnd || undefined,
      page: 1,
    })
  }

  const resetFilters = () => {
    setDraftEmail("")
    setDraftIp("")
    setDraftReason("")
    setDraftStart("")
    setDraftEnd("")
    navigate({
      search: {
        page: 1,
        pageSize,
        sortDesc: true,
      },
    })
  }

  const downloadCsv = () => {
    const token = localStorage.getItem("access_token")
    const base = (OpenAPI.BASE || "").replace(/\/$/, "")
    const url = `${base}/api/v1/admin/auth-events.csv${buildQuery({
      user_id: search.user_id,
      email: search.email,
      ip: search.ip,
      event_type: search.event_type,
      failure_reason: search.failure_reason,
      start_time: search.start,
      end_time: search.end,
      sort_desc: search.sortDesc ?? true,
    })}`
    // Fetch with auth, blob → object URL → download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to export")
        return r.blob()
      })
      .then((blob) => {
        const a = document.createElement("a")
        const objUrl = URL.createObjectURL(blob)
        a.href = objUrl
        a.download = `auth_events_${Date.now()}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(objUrl)
      })
      .catch((err) => console.error(err))
  }

  const formatTs = (ts: string) => {
    const d = new Date(ts)
    return showLocal
      ? format(d, "yyyy-MM-dd HH:mm:ss")
      : d.toISOString().replace("T", " ").slice(0, 19)
  }

  const inputStyle = {
    padding: "6px 10px",
    fontSize: "0.875rem",
    borderRadius: "8px",
    background: "rgba(255,255,255,0.04)",
    color: sc.heading as string,
    border: "1px solid rgba(255,255,255,0.1)",
  } as const

  return (
    <Box>
      <Flex mb={6} align="center" gap={3} flexWrap="wrap">
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("audit.backToAdmin", "Back to Admin")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiShield fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("audit.title", "Audit log")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "audit.subtitle",
              "Login activity, password changes, and per-session actions.",
            )}
          </Text>
        </Box>
        <HStack gap={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
          >
            <FiDownload />
            <Text ml={2}>{t("audit.exportCsv", "Export CSV")}</Text>
          </Button>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => {
              eventsQuery.refetch()
              countQuery.refetch()
            }}
          >
            <FiRefreshCw />
          </VisionButton>
        </HStack>
      </Flex>

      {/* Filter bar */}
      <VisionCard mb={6}>
        <VStack align="stretch" gap={4} p={4}>
          <Flex gap={3} flexWrap="wrap">
            <Box flex="1" minW="180px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.email", "Email")}
              </Text>
              <Input
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="user@example.com"
                size="sm"
              />
            </Box>
            <Box flex="1" minW="160px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.ip", "IP address")}
              </Text>
              <Input
                value={draftIp}
                onChange={(e) => setDraftIp(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="1.2.3.4"
                size="sm"
              />
            </Box>
            <Box flex="1" minW="180px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.eventType", "Event type")}
              </Text>
              <select
                value={search.event_type ?? ""}
                onChange={(e) =>
                  updateSearch({
                    event_type:
                      (e.target.value as AuthEventType) || undefined,
                    page: 1,
                  })
                }
                style={{ ...inputStyle, width: "100%" }}
              >
                <option value="">
                  {t("audit.filter.any", "Any")}
                </option>
                {EVENT_TYPES.map((et) => (
                  <option key={et} value={et}>
                    {et}
                  </option>
                ))}
              </select>
            </Box>
            <Box flex="1" minW="160px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.reason", "Failure reason")}
              </Text>
              <Input
                value={draftReason}
                onChange={(e) => setDraftReason(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="invalid_password"
                size="sm"
              />
            </Box>
          </Flex>

          <Flex gap={3} flexWrap="wrap" align="end">
            <Box flex="1" minW="180px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.from", "From")}
              </Text>
              <input
                type="datetime-local"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                style={{ ...inputStyle, width: "100%" }}
              />
            </Box>
            <Box flex="1" minW="180px">
              <Text fontSize="xs" color={sc.textMuted} mb={1}>
                {t("audit.filter.to", "To")}
              </Text>
              <input
                type="datetime-local"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                style={{ ...inputStyle, width: "100%" }}
              />
            </Box>
            <HStack gap={2}>
              <Button size="sm" onClick={applyFilters} colorScheme="blue">
                {t("audit.filter.apply", "Apply")}
              </Button>
              <Button size="sm" variant="ghost" onClick={resetFilters}>
                {t("audit.filter.reset", "Reset")}
              </Button>
            </HStack>
          </Flex>

          {search.user_id && (
            <HStack gap={2}>
              <Badge colorScheme="purple" size="sm">
                {t("audit.filter.userIdActive", "Filtered to user")}: {search.user_id}
              </Badge>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => updateSearch({ user_id: undefined, page: 1 })}
              >
                {t("audit.filter.clearUser", "Clear user filter")}
              </Button>
            </HStack>
          )}
        </VStack>
      </VisionCard>

      {/* Active sessions panel */}
      <VisionCard mb={6}>
        <Flex justify="space-between" align="center" px={4} pt={4} pb={2}>
          <HStack gap={3}>
            <Heading size="md" color={sc.heading}>
              {t("audit.sessions.title", "Active sessions")}
            </Heading>
            <Badge colorScheme="green" size="sm">
              {activeSessionsQuery.data?.length ?? 0}
            </Badge>
          </HStack>
          <VisionButton
            variant="ghost"
            size="sm"
            onClick={() => activeSessionsQuery.refetch()}
          >
            <FiRefreshCw />
          </VisionButton>
        </Flex>
        {activeSessionsQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : (activeSessionsQuery.data ?? []).length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("audit.sessions.empty", "No active sessions.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader color={sc.textMuted} />
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.user", "User")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.started", "Started")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.duration", "Duration")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.lastActivity", "Last activity")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.ip", "IP")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.sessions.col.actions", "Actions")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(activeSessionsQuery.data ?? []).map((s) => {
                  const concurrent = (activeCountByUser.get(s.user_id) ?? 0) > 1
                  const startedAt = new Date(s.started_at)
                  const lastAct = new Date(s.last_activity_at)
                  const durationMs = Date.now() - startedAt.getTime()
                  const hh = Math.floor(durationMs / 3600_000)
                  const mm = Math.floor((durationMs % 3600_000) / 60_000)
                  const isExpanded = expandedSessionId === s.id
                  return (
                    <>
                      <Table.Row key={s.id}>
                        <Table.Cell>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() =>
                              setExpandedSessionId(isExpanded ? null : s.id)
                            }
                          >
                            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                          </Button>
                        </Table.Cell>
                        <Table.Cell color={sc.heading} fontSize="sm">
                          <VStack align="start" gap={1}>
                            <Text>{s.user_email}</Text>
                            <HStack gap={1}>
                              {s.user_is_superuser && (
                                <Badge colorScheme="purple" size="sm">
                                  {t("audit.sessions.badge.superuser", "Superuser")}
                                </Badge>
                              )}
                              {concurrent && (
                                <Badge colorScheme="orange" size="sm">
                                  {t(
                                    "audit.sessions.badge.concurrent",
                                    "Concurrent",
                                  )}{" "}
                                  ×{activeCountByUser.get(s.user_id)}
                                </Badge>
                              )}
                              {s.ip_change_count > 0 && (
                                <Badge
                                  colorScheme="yellow"
                                  size="sm"
                                  title={t(
                                    "audit.sessions.badge.ipChangedTitle",
                                    "IP address changed during this session",
                                  )}
                                >
                                  {t(
                                    "audit.sessions.badge.ipChanged",
                                    "IP changed",
                                  )}{" "}
                                  ×{s.ip_change_count}
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                        </Table.Cell>
                        <Table.Cell color={sc.heading} fontSize="sm">
                          {showLocal
                            ? format(startedAt, "yyyy-MM-dd HH:mm:ss")
                            : startedAt
                                .toISOString()
                                .replace("T", " ")
                                .slice(0, 19)}
                        </Table.Cell>
                        <Table.Cell color={sc.heading} fontSize="sm">
                          {hh > 0 ? `${hh}h ` : ""}
                          {mm}m
                        </Table.Cell>
                        <Table.Cell color={sc.textMuted} fontSize="sm">
                          {formatDistanceToNow(lastAct, { addSuffix: true })}
                        </Table.Cell>
                        <Table.Cell
                          color={sc.heading}
                          fontFamily="mono"
                          fontSize="sm"
                          title={
                            s.ip_chain && s.ip_chain.length > 1
                              ? t("audit.col.ipChainTitle", "Proxy chain") +
                                ": " +
                                s.ip_chain.join(" → ")
                              : undefined
                          }
                        >
                          {s.ip_address ?? "—"}
                          {s.ip_chain && s.ip_chain.length > 1 && (
                            <Text
                              as="span"
                              fontSize="xs"
                              color={sc.textMuted}
                              ml={2}
                            >
                              (+{s.ip_chain.length - 1})
                            </Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={2}>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() =>
                                updateSearch({
                                  user_id: s.user_id,
                                  page: 1,
                                })
                              }
                              title={t(
                                "audit.sessions.viewEvents",
                                "Filter events for this user",
                              )}
                            >
                              {t("audit.sessions.events", "Events")}
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="red"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    t(
                                      "audit.sessions.confirmRevoke",
                                      "Force-end this session?",
                                    ),
                                  )
                                ) {
                                  revokeMutation.mutate(s.id)
                                }
                              }}
                              disabled={revokeMutation.isPending}
                            >
                              <FiLogOut />
                              <Text ml={1}>
                                {t("audit.sessions.revoke", "Revoke")}
                              </Text>
                            </Button>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                      {isExpanded && (
                        <Table.Row key={`${s.id}-acts`}>
                          <Table.Cell colSpan={7}>
                            <Box
                              p={3}
                              bg="rgba(255,255,255,0.02)"
                              borderRadius="md"
                            >
                              <HStack mb={2} gap={2}>
                                <Text fontSize="sm" color={sc.heading} fontWeight="bold">
                                  {t(
                                    "audit.sessions.activities",
                                    "Recent activities",
                                  )}
                                </Text>
                                <Text fontSize="xs" color={sc.textMuted}>
                                  ({s.user_agent ?? "—"})
                                </Text>
                              </HStack>
                              {activitiesQuery.isLoading ? (
                                <Spinner size="sm" color={sc.spinnerColor} />
                              ) : (activitiesQuery.data ?? []).length === 0 ? (
                                <Text color={sc.textMuted} fontSize="xs">
                                  {t(
                                    "audit.sessions.noActivities",
                                    "No mutations recorded for this session yet.",
                                  )}
                                </Text>
                              ) : (
                                <Box overflowX="auto">
                                  <Table.Root size="sm">
                                    <Table.Header>
                                      <Table.Row>
                                        <Table.ColumnHeader color={sc.textMuted}>
                                          {t("audit.sessions.act.time", "Time")}
                                        </Table.ColumnHeader>
                                        <Table.ColumnHeader color={sc.textMuted}>
                                          {t("audit.sessions.act.method", "Method")}
                                        </Table.ColumnHeader>
                                        <Table.ColumnHeader color={sc.textMuted}>
                                          {t("audit.sessions.act.path", "Path")}
                                        </Table.ColumnHeader>
                                        <Table.ColumnHeader color={sc.textMuted}>
                                          {t("audit.sessions.act.status", "Status")}
                                        </Table.ColumnHeader>
                                        <Table.ColumnHeader color={sc.textMuted}>
                                          {t("audit.sessions.act.dur", "ms")}
                                        </Table.ColumnHeader>
                                      </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                      {(activitiesQuery.data ?? []).map((a) => (
                                        <Table.Row key={a.id}>
                                          <Table.Cell
                                            color={sc.textMuted}
                                            fontSize="xs"
                                          >
                                            {showLocal
                                              ? format(
                                                  new Date(a.at),
                                                  "HH:mm:ss",
                                                )
                                              : a.at
                                                  .replace("T", " ")
                                                  .slice(11, 19)}
                                          </Table.Cell>
                                          <Table.Cell>
                                            <Badge size="sm">
                                              {a.method}
                                            </Badge>
                                          </Table.Cell>
                                          <Table.Cell
                                            color={sc.heading}
                                            fontFamily="mono"
                                            fontSize="xs"
                                          >
                                            {a.path}
                                          </Table.Cell>
                                          <Table.Cell
                                            color={
                                              a.status_code >= 400
                                                ? "red.300"
                                                : sc.heading
                                            }
                                            fontSize="xs"
                                          >
                                            {a.status_code}
                                          </Table.Cell>
                                          <Table.Cell
                                            color={sc.textMuted}
                                            fontSize="xs"
                                          >
                                            {a.duration_ms}
                                          </Table.Cell>
                                        </Table.Row>
                                      ))}
                                    </Table.Body>
                                  </Table.Root>
                                </Box>
                              )}
                            </Box>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>

      {/* Toolbar above the table */}
      <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={3}>
        <HStack gap={4}>
          <Text fontSize="sm" color={sc.textMuted}>
            {t("audit.results", "Results")}: <b>{total}</b>
          </Text>
          <HStack
            gap={2}
            align="center"
            as="button"
            onClick={() => setShowLocal((v) => !v)}
            cursor="pointer"
          >
            <Box
              w="18px"
              h="18px"
              borderRadius="4px"
              border="2px solid"
              borderColor={showLocal ? "blue.400" : sc.textMuted}
              bg={showLocal ? "blue.400" : "transparent"}
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="xs"
              color="white"
              fontWeight="bold"
            >
              {showLocal && "✓"}
            </Box>
            <Text fontSize="xs" color={sc.textMuted}>
              {t("audit.localTime", "Local time")}
            </Text>
          </HStack>
        </HStack>

        <HStack gap={2}>
          <select
            value={pageSize}
            onChange={(e) =>
              updateSearch({ pageSize: Number(e.target.value), page: 1 })
            }
            style={inputStyle}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / {t("audit.perPage", "page")}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="ghost"
            disabled={page <= 1}
            onClick={() => updateSearch({ page: page - 1 })}
          >
            <FiChevronLeft />
          </Button>
          <Text fontSize="sm" color={sc.heading}>
            {page} / {totalPages}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => updateSearch({ page: page + 1 })}
          >
            <FiChevronRight />
          </Button>
        </HStack>
      </Flex>

      <VisionCard>
        {eventsQuery.isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color={sc.spinnerColor} />
          </Flex>
        ) : eventsQuery.data && eventsQuery.data.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" py={4} px={4}>
            {t("audit.empty", "No auth events match these filters.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader
                    color={sc.textMuted}
                    cursor="pointer"
                    onClick={() =>
                      updateSearch({ sortDesc: !(search.sortDesc ?? true) })
                    }
                  >
                    {t("audit.col.time", "Timestamp")}{" "}
                    {(search.sortDesc ?? true) ? "↓" : "↑"}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.col.event", "Event")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.col.email", "Email")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.col.ip", "IP")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("audit.col.detail", "Detail")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {eventsQuery.data?.map((ev) => (
                  <Table.Row key={ev.id}>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {formatTs(ev.created_at)}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge colorScheme={eventColor[ev.event_type]} size="sm">
                        {ev.event_type}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {ev.email_attempted}
                    </Table.Cell>
                    <Table.Cell
                      color={sc.heading}
                      fontFamily="mono"
                      fontSize="sm"
                      title={
                        ev.ip_chain && ev.ip_chain.length > 1
                          ? t("audit.col.ipChainTitle", "Proxy chain") +
                            ": " +
                            ev.ip_chain.join(" → ")
                          : undefined
                      }
                    >
                      {ev.ip_address ?? "—"}
                      {ev.ip_chain && ev.ip_chain.length > 1 && (
                        <Text
                          as="span"
                          fontSize="xs"
                          color={sc.textMuted}
                          ml={2}
                        >
                          (+{ev.ip_chain.length - 1})
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell color={sc.textMuted} fontSize="xs" maxW="320px">
                      {(() => {
                        const meta = ev.event_metadata
                        const licenseKey =
                          meta && typeof meta.license_key === "string"
                            ? meta.license_key
                            : null
                        if (ev.failure_reason) {
                          return (
                            <Text as="span" color="red.300">
                              {ev.failure_reason}
                            </Text>
                          )
                        }
                        if (licenseKey) {
                          const source =
                            meta && typeof meta.source === "string"
                              ? meta.source
                              : null
                          const deviceId =
                            meta && typeof meta.device_id === "string"
                              ? meta.device_id
                              : null
                          const sub = [
                            source,
                            deviceId
                              ? t("audit.detail.device", "device {{id}}", {
                                  id: deviceId,
                                })
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                          return (
                            <VStack align="start" gap={0}>
                              <Text
                                as="span"
                                fontFamily="mono"
                                color={sc.heading}
                                title={licenseKey}
                                css={{ wordBreak: "break-all" }}
                              >
                                {licenseKey}
                              </Text>
                              {sub && (
                                <Text
                                  as="span"
                                  fontSize="2xs"
                                  color={sc.textMuted}
                                >
                                  {sub}
                                </Text>
                              )}
                            </VStack>
                          )
                        }
                        if (ev.user_agent) {
                          return (
                            <Text
                              title={ev.user_agent}
                              css={{
                                display: "inline-block",
                                maxWidth: "300px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                verticalAlign: "bottom",
                              }}
                            >
                              {ev.user_agent}
                            </Text>
                          )
                        }
                        return "—"
                      })()}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VisionCard>
    </Box>
  )
}
